import { Component, ComponentFactoryResolver, EventEmitter, OnInit, Output } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { IFormFieldComponent } from '../../../interfaces/iform-field-component';
import { FieldDefinition } from '../../../models/form-builder/field-definition.model';
import { FormFieldTypeEnum, ShortTextFieldType } from '../../../models/form-builder/form-field-types';
import { FormField } from '../../../models/form-builder/form-field.model';
import { GridCellContextMenuInfo } from '../../../models/grid/cell-context-menu-info.model';
import { CurrentSiteService } from '../../../services/current-site.service';
import { FieldDefinitionService } from '../../../services/field-definition.service';
import { FormTemplateInstancesService } from '../../../services/form-template-instances.service'
import { FlexibleSelectionFieldService } from '../../../services/flexible-selection-field.service';
import { FieldTypeAndName, FormFieldTypeAndNameService } from '../../../services/form-field-type-and-name.service';
import { FlexibleSelectionFieldConfigurationComponent } from '../../form-builder/field-configuration/flexible-selection-field-configuration/flexible-selection-field-configuration.component';
import { FieldDesignChangeInfo } from '../../form-builder/field-configuration/field-design-change-info';
import { FlexibleSelectionFieldConfigurationData, FormFieldListConstraintColumn } from '../../form-builder/field-configuration/flexible-selection-field-configuration/extras';
import { FormFieldBaseComponent } from '../form-field-base/form-field-base.component';
import { GridDesignerEditorBase } from './grid-designer-editor-base';
import { AlertDialogComponent, AlertDialogModel } from '../../../dialogs/alert/alert-dialog.component';
import { ReorderGridColumnsDialog } from '../../../dialogs/reorder-grid-columns/reorder-grid-columns.dialog';


@Component({
    selector: 'app-grid-field-designer',
    templateUrl: './grid-field-designer.component.html',
    styleUrls: ['../grid-form-field/grid-form-field.component.scss', './grid-field-designer.component.scss'],
    standalone: false
})
export class GridFieldDesignerComponent extends GridDesignerEditorBase implements OnInit {
    // Inputs.
    //@Input() gridFormFieldComponent: GridFormFieldComponent;

    // NOTE:  THIS CLASS WILL BE IMPLEMENTED USING THE EXISTING CODE FROM CLASS GridFormFieldComponent,
    //        EXTRACTING THE LOGIC TO DESIGN A GRID FORM FIELD WITHIN THE FORM BUILDER'S DESIGN MODE.

    @Output() initialized = new EventEmitter(); 

    public constructor(formFieldTypeAndNameService: FormFieldTypeAndNameService,
        private dialog: MatDialog,
        fieldDefinitionService: FieldDefinitionService,
        private currentSiteService: CurrentSiteService,
        private flexibleSelectionFieldService: FlexibleSelectionFieldService,
        private formTemplateInstancesService: FormTemplateInstancesService,
        //private formFieldService: FormFieldService,
        resolver: ComponentFactoryResolver) {
        super(formFieldTypeAndNameService, fieldDefinitionService, resolver);
    }

    // Life cycle methods.
    public ngOnInit(): void {
        // TO DO:  MOVE 'design' mode-related ngOnInit() code from grid-form-field.component.ts. to this method.on
        this.initialized.emit();

    }

    // Begin called by my HTML code.
    // End called by my HTML code.

    // Begin methods for handling control events.

    /**
     * Event which is called when a user right-clicks on a grid column
     * @param mouseEvent - Contains the data associated with the click event that occurred when user right clicks on the grid column.
     * @param hshColumnDef - a form field which represents one of the columns in the grid
     */
    public gridColumnContextMenuClick(mouseEvent: MouseEvent, hshColumnDef: FormField): void
    {
        // 03-19-2025 note:  added the following 'if' to check if the attachments grid has a context menu.
        if ((this.gridFormFieldComponent != null) && (this.gridFormFieldComponent.gridCellContextMenu != null)) {
            // Stop propagation and default behavior.
            mouseEvent.stopPropagation();
            mouseEvent.preventDefault();

            // Create a context menu information object.
            {
                // Assemble context menu data.
                let gridCellContextMenuInfo = new GridCellContextMenuInfo();

                gridCellContextMenuInfo.mouseEvent = mouseEvent;

                gridCellContextMenuInfo.columnDef = hshColumnDef;

                for (let iComponent: number = 0; iComponent < this.gridFormFieldComponent.RuntimeMetadata.AllComponentsCount; iComponent++) {
                    let component: FormFieldBaseComponent = this.gridFormFieldComponent.RuntimeMetadata.getAllComponentsFormFieldComponent(iComponent);
                    let componentColDef: FormField = component.FormField;

                    if (hshColumnDef.gridColClientId == componentColDef.gridColClientId) {
                        gridCellContextMenuInfo.component = component;

                        break;
                    }
                }

                gridCellContextMenuInfo.contextMenuXPos = gridCellContextMenuInfo.mouseEvent.clientX + 'px';
                gridCellContextMenuInfo.contextMenuYPos = gridCellContextMenuInfo.mouseEvent.clientY + 'px';

                // Associate the data with the context menu and trigger the menu.
                this.gridFormFieldComponent.gridCellContextMenu.menuData = gridCellContextMenuInfo;

                this.gridFormFieldComponent.gridCellContextMenu.openMenu();
            } // block
        } // if
    }

    // PJH - 6/26/2024 - added for VNEXT-1312 - column reordering
    public reorderColumnsClicked() {
        let dialogRef = this.dialog.open(ReorderGridColumnsDialog, {
            maxWidth: "100%",
            data: {
                columns: this.GridColumnDefs
            }
        });

        dialogRef.afterClosed().subscribe(cols => {
            if (cols) {
                let reOrderedColumnDefs = [];
                for (let i = 0; i < cols.length; i++) {
                    let colDef = this.GridColumnDefs.find(x => { return x.id == cols[i].id });
                    colDef.fieldOrder = i;
                    reOrderedColumnDefs.push(colDef);
                }

                this.GridColumnDefs = reOrderedColumnDefs;

                this.gridFormFieldComponent.handleColumnOrderUpdate();
            }
        });

    }

    public handleFieldDefinitionChange(): void {
        this.gridFormFieldComponent.designChange.emit();
    }

    public getColumnTypeIconName(hshColumnDef: FormField): string {
        let strIconName: string = null;

        let fieldTypeAndName: FieldTypeAndName = this.formFieldTypeAndNameService.getFieldTypeAndField(hshColumnDef.fieldDefinitionClassName);
        if (!fieldTypeAndName) {
            let errorMsg: string = `GridFormFieldComponent.getColumnTypeIconName():  cannot find a field definition ` +
                `for field class '${hshColumnDef.fieldDefinitionClassName}'.`;
            this.raiseException(errorMsg);
        }
        strIconName = fieldTypeAndName.IconName;

        return strIconName;
    }
    public getColumnTypeIconType(hshColumnDef: FormField): string {
        let strIconType: string = null;

        let fieldTypeAndName: FieldTypeAndName = this.formFieldTypeAndNameService.getFieldTypeAndField(hshColumnDef.fieldDefinitionClassName);
        if (!fieldTypeAndName) {
            let errorMsg: string = `GridFormFieldComponent.getColumnTypeIconName():  cannot find a field definition ` +
                `for field class '${hshColumnDef.fieldDefinitionClassName}'.`;
            this.raiseException(errorMsg);
        }
        strIconType = fieldTypeAndName.iconType;

        return strIconType;
    }

    public getFieldTypeTitle(hshColumnDef: FormField): string {
        return this.formFieldTypeAndNameService.getFieldNameFromFieldType(hshColumnDef.fieldDefinitionClassName);
    }

    public gridColumnEdit(hshEventData: any, columnDef: FormField): void {
        //this.gridFormFieldComponent.gridColumnEdit(hshEventData, columnDef);
        // Since this component is responsible for passing on
        // notifications to child / column components, replace
        // the component in the event with this component.
        this.gridFormFieldComponent.RuntimeData.componentBeingEdited = hshEventData['component'];
        hshEventData['componentBeingEdited'] = hshEventData['component'];
        hshEventData['onEditSaveCallback'] = this.gridFormFieldComponent.onEditSave;
        hshEventData['properties'].push('gridColumnBehavior');
        hshEventData['properties'].push('gridColumnRoles');
        hshEventData['properties'].push('gridColumnViewRoles');

        // Pass this column's edit event to the parent form field wrapper.
        this.gridFormFieldComponent.onEdit.emit(hshEventData);
    }

    public onCellDoubleClick(columnDef: FormField): void {
        let formTemplateId = columnDef.formTemplateId;
        if (formTemplateId == null) {
            formTemplateId = parseInt(window.location.pathname.split('/').pop());
        }

        if (formTemplateId == 0) {
            let message = 'Please save the form template before configuring flexible selection fields in a grid.';
            let title = 'Please Save'
            this.dialog.open(AlertDialogComponent, {
                disableClose: true,
                maxWidth: "600px",
                data: new AlertDialogModel(title, message)
            });
            return;
        }

        this.formTemplateInstancesService.getFields(formTemplateId, this.gridFormFieldComponent.formField.id).then(formFields => {
            let sourceFormFields: FormField[] = this.GridColumnDefs;
            for (let index: number = 0; index < formFields.length; index++) {
                let ff = formFields[index];
                if (ff.fieldDefinitionClassName != FormFieldTypeEnum.GridFieldType && sourceFormFields.filter(sff => sff.id == ff.id).length == 0) {
                    sourceFormFields.push(ff);
                }
            }

            let ff = columnDef;
            const nonFlexField: FormField = formFields.find(x => x.id == ff.dependsOnParentFormFieldId);
            if (ff && ff.fieldHasConfiguration) {
                const data: FlexibleSelectionFieldConfigurationData = {
                    formField: ff,
                    formFields: sourceFormFields,
                    formTemplateId: formTemplateId,
                    listValuesConstraintName: ff.listValuesConstraintName,
                    dependsOnParentFormFieldName: ff.dependsOnParentFormFieldName,
                    ListConstraintColumnId: ff.ListConstraintColumnId,
                    CascadingDropdownConstraintId: ff.cascadingDropdownConstraintId,
                    fieldColumnPairs: ff.formFieldListConstraintColumns,
                    sourceFieldConstraints: nonFlexField?.formFieldListConstraintColumns ?? []
                };

                let dialogConfig: MatDialogConfig = new MatDialogConfig();
                dialogConfig.disableClose = true;
                dialogConfig.data = data;

                const dialogRef = this.dialog.open(FlexibleSelectionFieldConfigurationComponent, dialogConfig);
                dialogRef.afterClosed().subscribe(result => {
                    const designChangeInfo = new FieldDesignChangeInfo();
                    designChangeInfo.saveDesignChange = true;

                    if (result != null) {
                        if (ff.dependsOnParentFormFieldId) {
                            this.flexibleSelectionFieldService.SetDependsOnParentFormFieldId(ff.id, ff.dependsOnParentFormFieldId);
                        }
                        if (result.name) {
                            ff.name = result.name;
                        }
                        ff.formFieldListConstraintColumns = [];
                        for (let index: number = 0; index < result.fieldColumnPairs.length; index++) {
                            let columnId = result.fieldColumnPairs[index].listConstraintColumnId;
                            if (ff.formFieldListConstraintColumns.filter(col => col.formFieldId == ff.id && col.listConstraintColumnId == columnId).length == 0) {
                                let newCol = new FormFieldListConstraintColumn(ff.id, columnId);
                                newCol.listConstraintColumnName = result.fieldColumnPairs[index].listConstraintColumnName;
                                ff.formFieldListConstraintColumns.push(newCol);
                            }
                        }

                        // PJH - for VNEXT-1670 (configure non-flex parent fields with constraints)
                        // See https://community-dc.max.gov/x/CASmn for overview of what this next bit is for
                        if (result.sourceFieldIsNotFlexField) {
                            const nonFlexField: FormField = formFields.find(x => x.id == ff.dependsOnParentFormFieldId);
                            nonFlexField.formFieldListConstraintColumns = [];
                            for (let index: number = 0; index < result.sourceFieldConstraints.length; index++) {
                                let columnId = result.sourceFieldConstraints[index].listConstraintColumnId;
                                if (nonFlexField.formFieldListConstraintColumns.filter(col => col.formFieldId == nonFlexField.id && col.listConstraintColumnId == columnId).length == 0) {
                                    let newCol = new FormFieldListConstraintColumn(nonFlexField.id, columnId);
                                    newCol.listConstraintColumnName = result.sourceFieldConstraints[index].listConstraintColumnName;
                                    nonFlexField.formFieldListConstraintColumns.push(newCol);
                                }
                            }
                            designChangeInfo.updatedFlexFieldSourceField = nonFlexField;
                        }

                        this.gridFormFieldComponent.designChange.emit(designChangeInfo);
                    }
                });
            }
        });
    }

    public handleOnPropertyUpdated(formField: FormField): void {
        let columnDef: FormField = this.gridFormFieldComponent.GridConfig.ColumnDefs.find(ff => ff.name == formField.name);
        if (columnDef != null)
            GridFieldDesignerComponent.copyFormFieldUpdatesFromTo(formField, columnDef);
    }

    public displayDeleteIconForColumn(columnDef: FormField): boolean {
        let display: boolean = true;

        let fieldDefinition: FieldDefinition = this.fieldDefinitionService.getFieldDefinition(columnDef.fieldDefinitionClassName);
        if ((fieldDefinition != null) && (fieldDefinition.customLogicHandler != null))
            display = fieldDefinition.customLogicHandler.displayDeleteIconInGridFor(columnDef);

        return display;
    }

    public removeGridColumnClicked(hshColumnDef: FormField): void {
        this.gridFormFieldComponent.GridConfig.removeGridColumn(hshColumnDef.gridColClientId);
        this.gridFormFieldComponent.designChange.emit();
        this.gridFormFieldComponent.FormInstanceElement.UserUpdatedData = true;
    }

    public addGridColumnClicked(fieldDefinitionClassNameParam: string = ShortTextFieldType): void {
        this.gridFormFieldComponent.GridConfig.addGridColumn(fieldDefinitionClassNameParam);

        // In any fields are listening for field names, update them now.
        this.gridFormFieldComponent.designChange.emit();

        this.gridFormFieldComponent.FormInstanceElement.UserUpdatedData = true;
    }

    public onFieldDropped(eventData: any): void {
        // Emit an event so FormBuilder can flag itself as dirty
        let fieldDefinition: FieldDefinition = eventData['dragData'];
        this.addGridColumnClicked(fieldDefinition.className);
    }

    public getNumericTotalPreviewValue(iColIndex: number, colDef: FormField): string {
        // Note:  this method either returns a zero/'0', for numeric form
        //        fields, or a blank, for non - numeric form fields.
        let previewTotalValue: string = '';

        if (this.gridFormFieldComponent.RuntimeMetadata && (this.gridFormFieldComponent.RuntimeMetadata.AllComponentsCount > 0)) {
            if ((iColIndex >= 0) && (iColIndex < this.gridFormFieldComponent.RuntimeMetadata.AllComponentsCount)) {
                let component: IFormFieldComponent = this.gridFormFieldComponent.RuntimeMetadata.getAllComponentsFormFieldComponent(iColIndex);

                if (component.hasNumericData()) {
                    previewTotalValue = '0';
                }
            }
        }

        return (previewTotalValue);
    }
    // End methods for handling control events.

    // Helper methods.
    private static copyFormFieldUpdatesFromTo(formField: FormField, columnDef: FormField): void {
        // Copy constraint-related fields for the time being.
        columnDef.selectOptions = formField.selectOptions;
        columnDef.selectOptionsConstraintName = formField.selectOptionsConstraintName;
        columnDef.selectOptionsConstraintValue = formField.selectOptionsConstraintValue;

        columnDef.regularExpressionConstraintName = formField.regularExpressionConstraintName;
        columnDef.regularExpressionConstraintValue = formField.regularExpressionConstraintValue;

        columnDef.cascadingDropdownConstraintName = formField.cascadingDropdownConstraintName;
        columnDef.cascadingDropdownConstraintValue = formField.cascadingDropdownConstraintValue;

        columnDef.numericRangeConstraintMinValue = formField.numericRangeConstraintMinValue;
        columnDef.numericRangeConstraintMaxValue = formField.numericRangeConstraintMaxValue;
        columnDef.numericRangeConstraintName = formField.numericRangeConstraintName;

        columnDef.dateRangeConstraintMinDate = formField.dateRangeConstraintMinDate;
        columnDef.dateRangeConstraintMaxDate = formField.dateRangeConstraintMaxDate;
        columnDef.dateRangeConstraintName = formField.dateRangeConstraintName;
    }

}
